import { useState, useRef, useCallback, useEffect } from 'react'
import type { BeforeAfterPair } from '@/data/portfolio'

interface BeforeAfterProps {
  pairs: BeforeAfterPair[]
}

function BeforeAfterSlider({ before, after, label }: BeforeAfterPair) {
  const [position, setPosition] = useState(50)
  const containerRef = useRef<HTMLDivElement>(null)
  const isDragging = useRef(false)
  const [containerWidth, setContainerWidth] = useState<number | null>(null)

  useEffect(() => {
    if (!containerRef.current) return
    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width)
    })
    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  const updatePosition = useCallback((clientX: number) => {
    if (!containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width))
    setPosition((x / rect.width) * 100)
  }, [])

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      isDragging.current = true
      ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
      updatePosition(e.clientX)
    },
    [updatePosition]
  )

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging.current) return
      updatePosition(e.clientX)
    },
    [updatePosition]
  )

  const handlePointerUp = useCallback(() => {
    isDragging.current = false
  }, [])

  return (
    <div className="space-y-3">
      {label && <p className="text-muted-foreground text-sm font-medium">{label}</p>}
      <div
        ref={containerRef}
        className="relative aspect-[16/10] cursor-col-resize overflow-hidden rounded-lg border shadow-md select-none"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        {/* After image (full width, behind) */}
        <img
          src={after}
          alt="After redesign"
          className="absolute inset-0 h-full w-full object-cover object-top"
          draggable={false}
        />

        {/* Before image (clipped) */}
        <div className="absolute inset-0 overflow-hidden" style={{ width: `${position}%` }}>
          <img
            src={before}
            alt="Before redesign"
            className="absolute inset-0 h-full w-full object-cover object-top"
            style={{ width: containerWidth ? `${containerWidth}px` : '100vw' }}
            draggable={false}
          />
        </div>

        {/* Slider line */}
        <div className="absolute top-0 bottom-0 z-10 w-0.5 bg-white shadow-lg" style={{ left: `${position}%` }}>
          {/* Handle */}
          <div className="absolute top-1/2 left-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-lg">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="text-gray-600">
              <path
                d="M7 4L3 10L7 16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13 4L17 10L13 16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        {/* Labels */}
        <div className="absolute top-3 left-3 z-10 rounded-full bg-black/60 px-2.5 py-1 text-xs font-medium text-white">
          Before
        </div>
        <div className="absolute top-3 right-3 z-10 rounded-full bg-black/60 px-2.5 py-1 text-xs font-medium text-white">
          After
        </div>
      </div>
    </div>
  )
}

export function BeforeAfter({ pairs }: BeforeAfterProps) {
  return (
    <section className="content-section space-y-6">
      <h2 className="text-2xl font-semibold">The Transformation</h2>
      <p className="text-muted-foreground">Drag the slider to compare the original site with our redesign.</p>
      <div className="space-y-8">
        {pairs.map((pair, i) => (
          <BeforeAfterSlider key={i} {...pair} />
        ))}
      </div>
    </section>
  )
}
